import { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Info } from 'lucide-react';
import type { RiceItem } from '../types';

interface ScoringViewProps {
  item: RiceItem;
  itemIndex: number;
  totalItems: number;
  phase: 'reach' | 'impact' | 'confidence' | 'effort';
  onScoreUpdate: (field: 'reach' | 'impact' | 'confidence' | 'effort', value: number | null) => void;
  onNext: () => void;
  onBack: () => void;
  canProceed: boolean;
  progress: number;
}

export function ScoringView({
  item,
  itemIndex,
  totalItems,
  phase,
  onScoreUpdate,
  onNext,
  onBack,
  canProceed,
  progress,
}: ScoringViewProps) {
  const [inputValue, setInputValue] = useState<string>(
    phase === 'reach' ? (item.reach?.toString() || '') :
    phase === 'impact' ? (item.impact?.toString() || '') :
    phase === 'confidence' ? (item.confidence?.toString() || '') :
    (item.effort?.toString() || '')
  );

  useEffect(() => {
    const value = 
      phase === 'reach' ? (item.reach?.toString() || '') :
      phase === 'impact' ? (item.impact?.toString() || '') :
      phase === 'confidence' ? (item.confidence?.toString() || '') :
      (item.effort?.toString() || '');
    setInputValue(value);
  }, [phase, item]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    const numValue = value.trim() === '' ? null : parseFloat(value);
    if (numValue !== null && !isNaN(numValue)) {
      onScoreUpdate(phase, numValue);
    } else {
      onScoreUpdate(phase, null);
    }
  };

  const getPhaseInfo = () => {
    switch (phase) {
      case 'reach':
        return {
          title: 'Reach',
          description: 'Estimated number of people or events affected in a given time period. Use a consistent unit (e.g., users/month).',
          placeholder: 'e.g., 1200',
          example: '1200 users per month',
        };
      case 'impact':
        return {
          title: 'Impact',
          description: 'Effect on each person/event. Use the scale: 3 = massive, 2 = high, 1 = medium, 0.5 = low, 0.25 = minimal.',
          placeholder: '3, 2, 1, 0.5, or 0.25',
          example: '3 (massive impact)',
        };
      case 'confidence':
        return {
          title: 'Confidence',
          description: 'How confident are you in your Reach & Impact estimates? Enter a percentage (0-100).',
          placeholder: 'e.g., 80',
          example: '80% confidence',
        };
      case 'effort':
        return {
          title: 'Effort',
          description: 'Required work, usually in person-months (or weeks/points if consistent). Higher effort → lower priority.',
          placeholder: 'e.g., 2',
          example: '2 person-months',
        };
    }
  };

  const phaseInfo = getPhaseInfo();

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-[var(--text-sec)] mb-2">
            <span>Item {itemIndex + 1} of {totalItems}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
            <div
              className="bg-[var(--slate)] h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Current Item */}
        <div className="mb-6 p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
          <h2 className="text-xl font-bold text-[var(--text-pri)] mb-2">{item.name}</h2>
          <div className="text-sm text-[var(--text-sec)]">
            {phase === 'reach' && <span>Reach: {item.reach !== null ? item.reach : 'Not set'}</span>}
            {phase === 'impact' && (
              <>
                <span>Reach: {item.reach}</span>
                <span className="mx-2">•</span>
                <span>Impact: {item.impact !== null ? item.impact : 'Not set'}</span>
              </>
            )}
            {phase === 'confidence' && (
              <>
                <span>Reach: {item.reach}</span>
                <span className="mx-2">•</span>
                <span>Impact: {item.impact}</span>
                <span className="mx-2">•</span>
                <span>Confidence: {item.confidence !== null ? `${item.confidence}%` : 'Not set'}</span>
              </>
            )}
            {phase === 'effort' && (
              <>
                <span>Reach: {item.reach}</span>
                <span className="mx-2">•</span>
                <span>Impact: {item.impact}</span>
                <span className="mx-2">•</span>
                <span>Confidence: {item.confidence}%</span>
                <span className="mx-2">•</span>
                <span>Effort: {item.effort !== null ? item.effort : 'Not set'}</span>
              </>
            )}
          </div>
        </div>

        {/* Phase Info */}
        <div className="mb-6">
          <div className="flex items-start gap-2 mb-3">
            <Info className="h-5 w-5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-pri)]">{phaseInfo.title}</h3>
              <p className="text-sm text-[var(--text-sec)] mt-1">{phaseInfo.description}</p>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              {phaseInfo.title} for <strong>{item.name}</strong>
            </label>
            <input
              type="number"
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={phaseInfo.placeholder}
              step={phase === 'impact' ? 0.25 : phase === 'confidence' ? 1 : 0.1}
              min={phase === 'confidence' ? 0 : 0}
              max={phase === 'confidence' ? 100 : undefined}
              className="w-full px-4 py-3 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              autoFocus
            />
            <p className="text-xs text-[var(--text-ter)] mt-1">Example: {phaseInfo.example}</p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back
          </button>
          <button
            onClick={onNext}
            disabled={!canProceed}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:text-[var(--text-ter)] disabled:cursor-not-allowed transition-colors"
          >
            {phase === 'effort' && itemIndex === totalItems - 1 ? 'Calculate Results' : 'Next'}
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}


